import md5 from 'md5';
import axios from 'axios';
import settings from '@/settings';

function calcRepetitionHash(repetition) {
    let parts = [
        repetition.app,
        repetition.list_id,
        repetition.name,
        repetition.repeats.map(r => {
            return r.when + ';' + r.done;
        })
    ];
    return md5(parts.join(';'));
}

function formatRepetition(repetition) {
    return {
        id: repetition.id,
        namespace: repetition.app,
        itemId: repetition.list_id,
        name: repetition.name,
        activeIndex: repetition.repeats.findIndex(r => !r.done),
        repeats: repetition.repeats.map(r => {
            return {
                when: new Date(r.when).getTime(),
                done: r.done
            };
        })
    };
}

class RepetitionHelper {
    constructor() {
        this.listeners = [];
        this.repetitionMap = new Map();

        this.polling = false;
        this.loaded = false;
        this.journeyIdChanges = null;
    }

    load(progressMappingRaw) {
        const { journeyIdChanges = null } = progressMappingRaw ? JSON.parse(progressMappingRaw) : {};
        this.journeyIdChanges = journeyIdChanges;

        this.loading = true;
        this.poll().then(() => {
            this.loaded = true;
        });
    }

    activate() {
        if (!this.loaded || this.polling) {
            return;
        }
        this.poll();
    }

    deactivate() {
        //
    }

    getRepetitions() {
        return [...this.repetitionMap.values()];
    }

    on(eventType, callback) {
        this.listeners.push([eventType, callback]);
    }

    poll() {
        let url = `${settings.endpoints.repetition}/repetitions/`;
        this.polling = true;
        return axios
            .get(url)
            .then(response => {
                // NOTE: some journey ids might change. If not fixed in database, it can be migrated here
                if (this.journeyIdChanges) {
                    response.data.forEach(x => {
                        if (x.app === 'memomaps-step-group') {
                            if (this.journeyIdChanges[x.list_id]) {
                                x.list_id = this.journeyIdChanges[x.list_id];
                            }
                        }
                    });
                }
                this.digestAndBroadcastChanges(response.data);
                this.polling = false;
            })
            .catch(error => {
                console.error(error); // eslint-disable-line no-console
                this.polling = false;
            });
    }

    digestAndBroadcastChanges(allRepetitions) {
        const events = [];
        const previousRepetitionKeys = Array.from(this.repetitionMap.keys());
        const updateSetOfRepetitionKeys = new Set();
        let formatedRepetition = null;
        allRepetitions.forEach(repetition => {
            const hash = calcRepetitionHash(repetition);
            if (this.repetitionMap.has(repetition.id)) {
                if (hash !== this.repetitionMap.get(repetition.id).hash) {
                    formatedRepetition = formatRepetition(repetition);
                    formatedRepetition.hash = hash;
                    events.push({ type: 'updated', data: formatedRepetition });
                }
            } else {
                formatedRepetition = formatRepetition(repetition);
                formatedRepetition.hash = hash;
                events.push({ type: 'created', data: formatedRepetition });
                this.repetitionMap.set(repetition.id, formatedRepetition);
            }
            updateSetOfRepetitionKeys.add(repetition.id);
        });
        previousRepetitionKeys.forEach(repId => {
            if (!updateSetOfRepetitionKeys.has(repId)) {
                const deletedRepetition = this.repetitionMap.get(repId);
                this.repetitionMap.delete(repId);
                events.push({
                    type: 'deleted',
                    data: { id: repId, namespace: deletedRepetition.namespace, itemId: deletedRepetition.itemId }
                });
            }
        });
        this.publishEvents(events);
    }

    publishEvents(events) {
        events.forEach(event => {
            this.listeners.forEach(listener => {
                const [eventType, func] = listener;
                if (eventType === event.type) {
                    func(event);
                } else if (eventType === 'changed') {
                    func(event);
                }
            });
        });
    }

    createRepetition(app, listId, name, limit, creationDate, link) {
        const url = `${settings.endpoints.repetition}/repetitions/`;
        let data = {
            app: app,
            list_id: listId,
            name: name
        };
        if (limit) {
            data.limit = limit;
        }
        if (link) {
            data.url = link;
        }
        if (creationDate) {
            data.created = new Date(creationDate).toISOString();
        }
        return axios
            .post(url, data)
            .then(response => response.data)
            .then(repetition => {
                let hash = calcRepetitionHash(repetition);
                repetition = formatRepetition(repetition);
                repetition.hash = hash;
                this.repetitionMap.set(repetition.id, repetition);
                this.publishEvents([{ type: 'created', data: repetition }]);
                return repetition.id;
            })
            .catch(err => {
                console.error(err); // eslint-disable-line no-console
            });
    }

    updateRepetition(repetitionId, app, listId, name) {
        const url = `${settings.endpoints.repetition}/repetitions/${repetitionId}/`;
        let data = {
            app: app,
            list_id: listId,
            name: name
        };
        return axios
            .put(url, data)
            .then(response => response.data)
            .then(repetition => {
                let hash = calcRepetitionHash(repetition);
                repetition = formatRepetition(repetition);
                repetition.hash = hash;
                this.repetitionMap.set(repetition.id, repetition);
                this.publishEvents([{ type: 'updated', data: repetition }]);
            });
    }

    deleteRepetition(repetitionId) {
        const url = `${settings.endpoints.repetition}/repetitions/${repetitionId}/`;
        return axios
            .delete(url)
            .then(response => response.data)
            .then(repetition => {
                this.repetitionMap.delete(repetitionId);
            });
    }
}

const repetitionHelper = new RepetitionHelper();
export default repetitionHelper;
